/**
 * Lista de inboxes para seleção de suspensão
 * Usado na correção de discrepâncias de limites
 * 
 * Versão: 1.0.0
 * Data: 2026-01-08
 */
import { Lock, MessageSquare, Star } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenantInboxesForAudit } from '@/hooks/useAdminTenantLimitsAudit';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface InboxSelectionListProps {
  tenantId: string;
  requiredCount: number;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

/**
 * Retorna a cor do badge baseado no tipo de canal
 */
function getChannelColor(channelType: string): string {
  switch (channelType) {
    case 'whatsapp': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'telegram': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'instagram': return 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400';
    case 'facebook': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
    default: return 'bg-muted text-muted-foreground';
  }
}

/**
 * Componente de lista de inboxes para seleção
 */
export function InboxSelectionList({
  tenantId,
  requiredCount,
  selectedIds,
  onSelectionChange,
}: InboxSelectionListProps) {
  const { data: inboxes, isLoading, error } = useTenantInboxesForAudit(tenantId);
  
  // Toggle seleção de uma inbox
  const toggleInbox = (inboxId: string, canDeactivate: boolean) => {
    if (!canDeactivate) return;
    
    if (selectedIds.includes(inboxId)) {
      // Remover da seleção
      onSelectionChange(selectedIds.filter(id => id !== inboxId));
    } else if (selectedIds.length < requiredCount) {
      // Adicionar à seleção (se ainda não atingiu o limite)
      onSelectionChange([...selectedIds, inboxId]);
    }
  };
  
  // Label do status
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Ativa';
      case 'suspended': return 'Suspensa';
      case 'inactive': return 'Inativa';
      case 'pending': return 'Pendente';
      default: return status;
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        Erro ao carregar inboxes: {error.message}
      </div>
    );
  }
  
  if (!inboxes || inboxes.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhuma inbox encontrada
      </div>
    );
  }
  
  const selectionComplete = selectedIds.length === requiredCount;
  
  return (
    <div className="space-y-2">
      {/* Instrução */}
      <div className="text-sm text-muted-foreground mb-4">
        Selecione <strong>{requiredCount}</strong> inbox(es) para suspender.
        {selectionComplete && (
          <span className="text-green-600 ml-2">✓ Seleção completa</span>
        )}
      </div>
      
      {/* Lista de inboxes */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {inboxes.map((inbox) => {
          const isSelected = selectedIds.includes(inbox.inbox_id);
          const canSelect = inbox.can_deactivate && (isSelected || selectedIds.length < requiredCount);
          
          return (
            <div
              key={inbox.inbox_id}
              className={`
                flex items-center gap-3 p-3 border rounded-lg transition-colors
                ${inbox.can_deactivate ? 'cursor-pointer hover:bg-muted/50' : 'opacity-60 cursor-not-allowed bg-muted/30'}
                ${isSelected ? 'border-destructive bg-destructive/5' : ''}
              `}
              onClick={() => toggleInbox(inbox.inbox_id, canSelect)}
            >
              {/* Checkbox ou Lock */}
              {inbox.can_deactivate ? (
                <Checkbox
                  checked={isSelected}
                  disabled={!canSelect}
                  onCheckedChange={() => toggleInbox(inbox.inbox_id, canSelect)}
                />
              ) : (
                <Lock className="h-4 w-4 text-muted-foreground" />
              )}
              
              {/* Ícone da inbox */}
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                {inbox.is_default ? (
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                ) : (
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              
              {/* Info da inbox */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{inbox.name}</span>
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${getChannelColor(inbox.channel_type)}`}
                  >
                    {inbox.channel_type}
                  </Badge>
                  {inbox.is_default && (
                    <Badge variant="secondary" className="text-xs">Padrão</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <span>{inbox.provider_slug}</span>
                  <span>•</span>
                  <span>{getStatusLabel(inbox.status)}</span>
                </div>
              </div>
              
              {/* Data de criação */}
              <div className="text-xs text-muted-foreground text-right">
                {format(new Date(inbox.created_at), "dd/MM/yyyy", { locale: ptBR })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
